import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { safeLocalStorage } from '@/lib/safe-storage';

export type CatalogViewMode = 'horizontal' | 'grid';
export type CatalogSortMode = 'default' | 'name-asc' | 'name-desc' | 'price-asc' | 'price-desc';

interface CatalogViewState {
  mobileViewMode: CatalogViewMode;
  sortMode: CatalogSortMode;
  setMobileViewMode: (mode: CatalogViewMode) => void;
  toggleMobileViewMode: () => void;
  setSortMode: (mode: CatalogSortMode) => void;

  // Hydration flag — критично для SSR/CSR consistency.
  // mobileViewMode из localStorage может отличаться от initial → hydration mismatch grid'а каталога.
  _hasHydrated: boolean;
  setHasHydrated: (value: boolean) => void;
}

export const useCatalogViewStore = create<CatalogViewState>()(
  persist(
    (set) => ({
      mobileViewMode: 'horizontal',
      sortMode: 'default',
      _hasHydrated: false,
      setMobileViewMode: (mode) => set({ mobileViewMode: mode }),
      toggleMobileViewMode: () =>
        set((state) => ({
          mobileViewMode: state.mobileViewMode === 'horizontal' ? 'grid' : 'horizontal',
        })),
      setSortMode: (mode) => set({ sortMode: mode }),
      setHasHydrated: (value) => set({ _hasHydrated: value }),
    }),
    {
      name: 'catalog-view',
      storage: createJSONStorage(() => safeLocalStorage),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

export const selectCatalogHasHydrated = (state: CatalogViewState) => state._hasHydrated;
// Безопасное чтение mobileViewMode — до rehydrate возвращает 'horizontal' (как на сервере).
export const selectMobileViewModeSafe = (state: CatalogViewState): CatalogViewMode =>
  state._hasHydrated ? state.mobileViewMode : 'horizontal';
